"use client";

import { useI18n } from "@/lib/i18n/I18nProvider";
import { Check, X, AlertCircle } from "lucide-react";

const COMPARISON_DATA = [
  {
    feature: "USALI 12th + USAR",
    keiront: true,
    excel: false,
    erp: "partial",
  },
  {
    feature: "Treasury + 13-week forecast",
    keiront: true,
    excel: false,
    erp: "partial",
  },
  {
    feature: "AI Analytics (4 languages)",
    keiront: true,
    excel: false,
    erp: false,
  },
  {
    feature: "1С XML Integration",
    keiront: true,
    excel: false,
    erp: "partial",
  },
  {
    feature: "PMS Integration (10+ systems)",
    keiront: true,
    excel: false,
    erp: "partial",
  },
  {
    feature: "Mobile App",
    keiront: "coming",
    excel: false,
    erp: "partial",
  },
  {
    feature: "Implementation Time",
    keiront: "2–4 weeks*",
    excel: "—",
    erp: "6-12 months",
  },
  {
    feature: "Cost",
    keiront: "$$",
    excel: "$*",
    erp: "$$$$",
  },
];

interface StatusCellProps {
  value: string | boolean;
  type: "feature" | "value";
}

function StatusCell({ value, type }: StatusCellProps): JSX.Element {
  if (type === "feature") {
    return (
      <div className="font-medium text-slate-900 text-sm">
        {typeof value === "string" ? value : ""}
      </div>
    );
  }

  // Handle different value types
  if (value === true) {
    return (
      <div className="flex items-center justify-center gap-2 bg-keiro-mist rounded-lg py-2 px-3">
        <Check className="w-5 h-5 text-keiro-green-dark" />
        <span className="text-sm font-medium text-keiro-green-dark">Built-in</span>
      </div>
    );
  }

  if (value === false) {
    return (
      <div className="flex items-center justify-center gap-2 bg-red-50 rounded-lg py-2 px-3">
        <X className="w-5 h-5 text-red-600" />
        <span className="text-sm font-medium text-red-600">No</span>
      </div>
    );
  }

  if (value === "partial") {
    return (
      <div className="flex items-center justify-center gap-2 bg-amber-50 rounded-lg py-2 px-3">
        <AlertCircle className="w-5 h-5 text-amber-700" />
        <span className="text-sm font-medium text-amber-700">Limited</span>
      </div>
    );
  }

  if (value === "coming") {
    return (
      <div className="flex items-center justify-center bg-blue-50 rounded-lg py-2 px-3">
        <span className="text-sm font-medium text-blue-700">Coming Soon</span>
      </div>
    );
  }

  // For strings like "4 weeks", "6-12 months", "$$", "$*", "—"
  return (
    <div className="text-center text-sm font-medium text-slate-700">
      {typeof value === "string" ? value : ""}
    </div>
  );
}

export function ComparisonSection(): JSX.Element {
  const { t, locale } = useI18n();
  const isEn = locale === "en";

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {t("landing.comparison.title")}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t("landing.comparison.subtitle")}
          </p>
        </div>

        {/* Comparison Table - Desktop */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-4 px-6 font-semibold text-slate-900">
                  {t("landing.comparison.feature")}
                </th>
                <th className="text-center py-4 px-4 font-semibold text-keiro-green-dark">
                  Keiront
                </th>
                <th className="text-center py-4 px-4 font-semibold text-slate-700">
                  {t("landing.comparison.excel")}
                </th>
                <th className="text-center py-4 px-4 font-semibold text-slate-700">
                  {t("landing.comparison.erp")}
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_DATA.map((row, index) => (
                <tr
                  key={index}
                  className={
                    index % 2 === 0
                      ? "bg-white border-b border-slate-100"
                      : "bg-slate-50/40 border-b border-slate-100"
                  }
                >
                  <td className="py-5 px-6">
                    <StatusCell value={row.feature} type="feature" />
                  </td>
                  <td className="py-5 px-4">
                    <StatusCell value={row.keiront} type="value" />
                  </td>
                  <td className="py-5 px-4">
                    <StatusCell value={row.excel} type="value" />
                  </td>
                  <td className="py-5 px-4">
                    <StatusCell value={row.erp} type="value" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Comparison Cards - Mobile */}
        <div className="lg:hidden space-y-4">
          {COMPARISON_DATA.map((row, index) => (
            <div
              key={index}
              className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-200"
            >
              <h3 className="font-semibold text-slate-900 text-sm">
                {row.feature}
              </h3>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-slate-500 mb-1 font-medium">Keiront</p>
                  <StatusCell value={row.keiront} type="value" />
                </div>
                <div>
                  <p className="text-slate-500 mb-1 font-medium">Excel</p>
                  <StatusCell value={row.excel} type="value" />
                </div>
                <div>
                  <p className="text-slate-500 mb-1 font-medium">ERP</p>
                  <StatusCell value={row.erp} type="value" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footnote */}
        <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-900 text-center">
            <span className="font-semibold">*</span>{" "}
            {isEn
              ? "Typical onboarding with prepared master data (PMS / accounting integration, reference data). Actual timeline depends on configuration and data readiness."
              : "Типовое внедрение при подготовленных мастер-данных (интеграция 1С, PMS, справочники). Точный срок зависит от конфигурации и готовности данных."}
          </p>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 bg-keiro-green hover:bg-keiro-green-dark text-white px-6 py-3 text-base font-semibold rounded-lg transition-colors"
          >
            {t("landing.cta.selectPricing")}
          </a>
        </div>
      </div>
    </section>
  );
}
